import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import classNames from "classnames";
import { useAuth } from "_hooks";
import { Icon } from "components/atoms";
import { Base } from "components/template";
import { getUser, WEB_APP } from "_config/constants/app.constants";
import logo from "_utilities/images/logo.png";

const prefix = "v-account-select";

const AccountSelect = () => {
  const navigate = useNavigate();
  const {
    workspaces,
    workspacesIsLoading,
    fetchWorkspaces,
    loginWorkspace,
    isLoggedIn,
    user,
    userIsLoading
  } = useAuth();

  const account = getUser(user);
  const workspacesId = account?.workspacesId;

  // Workspace seleccionado en curso: bloquea todos los botones y muestra spinner en
  // el elegido mientras loginWorkspace responde, hasta la redirection. Evita doble
  // clic y el parpadeo de vistas por navegaciones concurrentes.
  const [selectingId, setSelectingId] = useState(null);

  const items = Array.isArray(workspaces) ? workspaces : [];

  // Rehidratacion de sesion: al entrar directo/recargar, el store arranca en null.
  // Si no hay usuario se dispara isLoggedIn() (una sola vez) para recuperarlo desde
  // la cookie antes de poder listar los workspaces.
  const sessionRequested = useRef(false);

  // Marca cuando la verificacion de sesion ya RESOLVIO (isLoggedIn disparado y la
  // peticion termino). Se usa estado (no ref) para que la redireccion a login espere
  // a que userIsLoading transicione true->false y evitar el redirect prematuro.
  const [sessionChecked, setSessionChecked] = useState(false);

  const ensureSession = useCallback(() => {
    if (sessionRequested.current) return;
    if (user !== null || userIsLoading) return;
    sessionRequested.current = true;
    isLoggedIn();
  }, [isLoggedIn, user, userIsLoading]);

  useEffect(() => {
    ensureSession();
  }, [ensureSession]);

  // La verificacion resolvio cuando userIsLoading hizo la transicion true->false.
  // loadingSeen garantiza que primero se observo el estado de carga (startLog) antes
  // de aceptar el false como "resuelto": evita marcar sessionChecked en el render
  // inicial (donde userIsLoading aun es false y provocaria un redirect prematuro).
  const loadingSeen = useRef(false);

  useEffect(() => {
    if (userIsLoading) {
      loadingSeen.current = true;
      return;
    }
    if (loadingSeen.current) {
      setSessionChecked(true);
    }
  }, [userIsLoading]);

  // Sin sesion: una vez resuelta la verificacion, si no hay usuario no hay sesion
  // valida -> volver a login. Cubre la entrada directa/recarga a /account/select
  // sin cookie de sesion. Espera a sessionChecked para no redirigir prematuramente.
  useEffect(() => {
    if (!sessionChecked) return;
    if (user === null) navigate("/account/login");
  }, [sessionChecked, user, navigate]);

  // Garantiza la carga de workspaces aun cuando se entra directo a /account/select
  // (recarga/URL directa): cuando hay workspacesId en sesion y el store no tiene la
  // lista ni esta cargando, se dispara el fetch una sola vez.
  const requested = useRef(false);

  const ensureWorkspaces = useCallback(() => {
    if (requested.current) return;
    if (!Array.isArray(workspacesId) || workspacesId.length === 0) return;
    if (workspaces !== null || workspacesIsLoading) return;
    requested.current = true;
    fetchWorkspaces(workspacesId);
  }, [fetchWorkspaces, workspaces, workspacesIsLoading, workspacesId]);

  useEffect(() => {
    ensureWorkspaces();
  }, [ensureWorkspaces]);

  // Selecciona un workspace: aplica el cambio en backend y, si la respuesta es
  // exitosa (status.code === 0), redirige a WEB_APP. El thunk no redirige.
  const handleSelect = async (workspace) => {
    if (selectingId) return;
    setSelectingId(workspace?._id);
    const { status } = await loginWorkspace(workspace?._id);
    const code = Array.isArray(status) ? status?.[0]?.code : status?.code;
    if (code === 0) {
      const url = new URL(WEB_APP);
      url.searchParams.set("ws", workspace?._id);
      window.location.replace(url.toString());
      return;
    }
    // Fallo la seleccion: liberar los botones para reintentar.
    setSelectingId(null);
  };

  // No mostrar la vista hasta resolver la verificacion de sesion: cubre el primer
  // render (antes de disparar isLoggedIn), la peticion en curso y el caso sin sesion
  // (a punto de redirigir a login). Evita el flash de la vista sin datos.
  const checkingSession = user === null || userIsLoading;

  if (checkingSession) {
    return (
      <Base>
        <div className={`${prefix}__loading flex items-center justify-center w-full py-10`}>
          <Icon className="animate-spin text-primary text-6xl" icon="spinner" />
        </div>
      </Base>
    );
  }

  return (
    <Base>
      <div className={`${prefix} flex flex-col p-3 mx-auto w-full sm:w-2/3 md:w-3/5 lg:w-5/12 max-w-[550px]`}>
        <div className={`${prefix}__header flex flex-col gap-3 mb-5`}>
          <img src={logo} alt="nbsk-logo" className="w-[55%] mx-auto mb-5" />
          <p className="text-center text-3xl font-bold mx-auto">Selecciona tu cuenta</p>
          <p className="text-center text-xl mx-auto">Elige la cuenta con la que quieres continuar</p>
        </div>

        <div className={`${prefix}__grid flex flex-wrap justify-center gap-2`}>
          {items.map((workspace, index) => {
            const isSelecting = selectingId === workspace?._id;
            return (
              <button
                key={workspace?._id ?? index}
                type="button"
                onClick={() => handleSelect(workspace)}
                disabled={selectingId !== null}
                className={classNames(
                  `${prefix}__card`,
                  "flex flex-col items-center justify-between gap-2 p-3 rounded-lg border border-primary bg-primary text-white",
                  "h-[120px] overflow-hidden",
                  "w-full sm:w-[calc(50%-0.25rem)] lg:w-[calc(25%-0.375rem)]",
                  "transition ease-in-out duration-300",
                  selectingId === null && "hover:scale-105 hover:border-2",
                  selectingId !== null && !isSelecting && "opacity-50",
                  "disabled:cursor-not-allowed"
                )}
              >
                {isSelecting ? (
                  <div className={`${prefix}__card-loading flex items-center justify-center w-full h-full`}>
                    <Icon className="animate-spin text-white text-4xl" icon="spinner" />
                  </div>
                ) : (
                  <>
                    <div className={`${prefix}__card-logo flex items-center justify-center w-full h-[45px] shrink-0`}>
                      {workspace?.lookAndFeel?.customerLogo && (
                        <img
                          src={workspace.lookAndFeel.customerLogo}
                          alt={workspace?.lookAndFeel?.account?.name ?? workspace?._id ?? "workspace"}
                          className="max-h-full max-w-full object-contain"
                        />
                      )}
                    </div>
                    <span className={classNames(
                      `${prefix}__card-name`,
                      "flex-1 flex items-center justify-center w-full min-w-0"
                    )}>
                      <span className="text-center text-sm font-bold w-full truncate">
                        {workspace?.lookAndFeel?.account?.name ?? workspace?._id}
                      </span>
                    </span>
                  </>
                )}
              </button>
            );
          })}
        </div>
      </div>
    </Base>
  );
};

export default AccountSelect;
